import { useMemo } from 'react';
import { buildScopedClient, isBiFolderSegment } from '@/shared';
import { ClientProps } from '@/types';
import { useClient } from './ClientContext';
import { useCorporateFlags } from './CorporateFlagsContext';

/**
 * Cliente escopado para a loja/segmento ativos (`selectedBrandPair`), via `buildScopedClient`.
 * Use dentro de `CorporateFlagsProvider`. Retorna `null` quando a combinação é inválida (ver `clientScope.ts`).
 */
export function useSelectedClientScope(): ClientProps | null {
  const { selectedClient } = useClient();
  const { selectedBrandPair } = useCorporateFlags();

  return useMemo(() => {
    const scoped = buildScopedClient(selectedClient, selectedBrandPair);
    if (isBiFolderSegment(selectedBrandPair?.codSegmento)) {
      return scoped ?? selectedClient ?? null;
    }
    return scoped;
  }, [selectedClient, selectedBrandPair]);
}

/**
 * Mesmo escopo que `useSelectedClientScope`, com fallback para `selectedClient` (raiz)
 * quando o escopo estrito é `null` — alinhado ao uso anterior `buildScopedClient(...) ?? selectedClient` em cards/UI.
 */
export function useSelectedClientScopeWithFallback(): ClientProps {
  const { selectedClient } = useClient();
  const scoped = useSelectedClientScope();

  return useMemo(() => (scoped ?? selectedClient) as ClientProps, [scoped, selectedClient]);
}
